import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "./styles.scss";
import SearchBar from "./SearchBar";
import TrackedShipments from "./TrackedShipments";
import { TrackingDetailsCard } from "../../components/TrackingDetails";
import { getTrackingUrl } from "../../config/api-config";
import { withClientContext } from "../../utils/clientContext";

/** Tracking ID of an API result. */
const getResultId = (result) =>
  result?.orderInformation?.trackingId || result?.statuses?.[0]?.trackingId;

/** True when an API result belongs to the given AWB / child AWB number. */
const matchesId = (result, id) =>
  [
    result?.orderInformation?.trackingId,
    result?.orderInformation?.cAwbNumber,
    result?.statuses?.[0]?.trackingId,
    result?.statuses?.[0]?.cAwbNumber,
  ].includes(id);

const fetchTracking = async (awbNumber) => {
  try {
    const response = await fetch(withClientContext(getTrackingUrl(awbNumber)));
    return await response.json();
  } catch (error) {
    return null;
  }
};

const hasStatuses = (data) => Array.isArray(data?.statuses) && data.statuses.length > 0;

const PackageIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M21 8 12 3 3 8m18 0v8l-9 5m9-13-9 5m0 8-9-5V8m9 13v-8M3 8l9 5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

function ShipmentTracker() {
  const { trackingId } = useParams();
  const [searchParams] = useSearchParams();
  const [trackingResults, setTrackingResults] = useState([]);
  const [activeTracking, setActiveTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const initialLoadRef = useRef(false);

  const trackAwbNumbers = useCallback(async (value) => {
    const awbNumbers = [...new Set(value.trim().split(/[\s,]+/).filter(Boolean))];
    if (awbNumbers.length === 0) return;

    setTrackingResults([]);
    setActiveTracking(null);
    setLoading(true);

    const results = (await Promise.all(awbNumbers.map(fetchTracking))).filter(
      hasStatuses
    );
    setLoading(false);
    if (results.length === 0) return;

    setTrackingResults(results);
    // Show the last shipment in the list
    setActiveTracking(getResultId(results[results.length - 1]));
  }, []);

  // Track the AWB from the URL once (?d=…, ?shipmentNo=… or /:trackingId)
  useEffect(() => {
    const trackingNumber =
      searchParams.get("d") || searchParams.get("shipmentNo") || trackingId;
    if (trackingNumber && !initialLoadRef.current) {
      initialLoadRef.current = true;
      trackAwbNumbers(trackingNumber);
    }
  }, [trackingId, searchParams, trackAwbNumbers]);

  const removeAwbNumber = (id) => {
    const remaining = trackingResults.filter((r) => !matchesId(r, id));
    setTrackingResults(remaining);
    if (remaining.length === 0) {
      setActiveTracking(null);
    } else if (activeTracking === id) {
      setActiveTracking(getResultId(remaining[0]));
    }
  };

  const clearAll = () => {
    setTrackingResults([]);
    setActiveTracking(null);
  };

  const activeResult =
    (activeTracking && trackingResults.find((r) => matchesId(r, activeTracking))) ||
    null;

  return (
    <div className="tracker-page">
      <div className={`tracker-wrapper ${activeResult ? "" : "empty"}`}>
        {activeResult ? (
          <SearchBar onTrack={trackAwbNumbers} loading={loading} />
        ) : (
          <div className="card search-card">
            <span className="search-card-icon" aria-hidden="true">
              <PackageIcon />
            </span>
            <h1>Track your shipment</h1>
            <SearchBar onTrack={trackAwbNumbers} loading={loading} />
          </div>
        )}

        <TrackedShipments
          ids={trackingResults.map(getResultId)}
          activeId={activeTracking}
          onView={setActiveTracking}
          onRemove={removeAwbNumber}
          onClearAll={clearAll}
        />

        {activeResult && <TrackingDetailsCard trackingData={activeResult} />}
      </div>
    </div>
  );
}

export default ShipmentTracker;
